import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AppContext } from "./context/AppContext";

export default function LandingScreen() {
  const navigate = useNavigate();
  const { player, setPlayer, isGuest, setIsGuest, setGuestPlays, guestPlays, logOut, credits, setCredits, userId } = useContext(AppContext);
  const canPlay = !!(player && !isGuest);
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentMsg, setPaymentMsg] = useState("");
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    if (searchParams.get("payment") !== "success" || !userId) return;
    axios.post(`${process.env.REACT_APP_API_BASE}/api/auth/refresh`, { userId })
      .then(res => setCredits(res.data.credits ?? 0))
      .catch(() => {});
    setPaymentMsg("Payment successful! Your game credit has been added.");
    setSearchParams({});
  }, []);

  const handleBuyGame = async (bundle) => {
    if (!userId) return;
    setBuying(bundle);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE}/api/payment/create-checkout`, { userId, bundle });
      window.location.href = res.data.url;
    } catch {
      setBuying(false);
    }
  };

  const handleGuestLogin = () => {
    setPlayer("Guest");
    setIsGuest(true);
    setGuestPlays({ presidents: 0, states: 0, leaders: 0 });
  };

  const GUEST_LIMIT = 1;
  const guestPlaysLeft = (key) => Math.max(0, GUEST_LIMIT - (guestPlays[key] || 0));

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes arcadeFlash {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
      `}</style>
      <div style={styles.card}>
        <header style={styles.header}>
          <h1 style={styles.title}>AI Arcade</h1>
          {player ? (
            <div style={styles.userBadge}>
              {isGuest ? "Playing as Guest" : <>Logged in as <strong>{player}</strong></>}
              {!isGuest && (
                <span style={credits > 0 ? styles.creditBadgeFunded : styles.creditBadgeEmpty}>
                  {credits} {credits === 1 ? "credit" : "credits"}
                </span>
              )}
              <button style={styles.linkBtn} onClick={logOut}>Log out</button>
            </div>
          ) : (
            <p style={styles.subtitle}>Log in, create an account, or play as a guest</p>
          )}
          {paymentMsg && <p style={styles.paymentSuccess}>{paymentMsg}</p>}
        </header>

        <main style={styles.main}>
          {/* Presidents game card */}
          <button
            style={{
              ...styles.gameCard,
              ...((canPlay || isGuest) ? {} : styles.gameCardDisabled)
            }}
            disabled={!player}
            onClick={() => navigate("/game")}
          >
            <span style={styles.gameIcon}>🎩</span>
            <div>
              <div style={styles.gameName}>Guess the US President</div>
              <div style={styles.gameDesc}>
                Chat with a mystery president and figure out who it is before your guesses run out.
              </div>
            </div>
            {!player && <div style={styles.lockBadge}>🔒 Log in to play</div>}
            {isGuest && <div style={styles.guestBadge}>{guestPlaysLeft("presidents")} plays left</div>}
          </button>

          {/* State game card */}
          <button
            style={{
              ...styles.gameCard,
              ...((canPlay || isGuest) ? styles.gameCardState : styles.gameCardDisabled),
              marginTop: 12
            }}
            disabled={!player}
            onClick={() => navigate("/state-game")}
          >
            <span style={styles.gameIcon}>🗺️</span>
            <div>
              <div style={{...styles.gameName, color: "#39ff14", textShadow: "0 0 8px #39ff14, 0 0 20px #39ff14"}}>Guess the US State</div>
              <div style={styles.gameDesc}>
                Chat with a mystery state and figure out which one it is before your guesses run out.
              </div>
            </div>
            {!player && <div style={styles.lockBadge}>🔒 Log in to play</div>}
            {isGuest && <div style={styles.guestBadge}>{guestPlaysLeft("states")} plays left</div>}
          </button>

          {/* Leaders game card */}
          <button
            style={{
              ...styles.gameCard,
              ...((canPlay || isGuest) ? styles.gameCardLeaders : styles.gameCardDisabled),
              marginTop: 12
            }}
            disabled={!player}
            onClick={() => navigate("/leaders-game")}
          >
            <span style={styles.gameIcon}>👑</span>
            <div>
              <div style={{...styles.gameName, color: "#bf00ff", textShadow: "0 0 8px #bf00ff, 0 0 20px #bf00ff"}}>Guess the Famous Leader</div>
              <div style={styles.gameDesc}>
                5 random world leaders each round — chat with the mystery one and identify them.
              </div>
            </div>
            {!player && <div style={styles.lockBadge}>🔒 Log in to play</div>}
            {isGuest && <div style={styles.guestBadge}>{guestPlaysLeft("leaders")} plays left</div>}
          </button>
        </main>

        <div style={styles.highScoreBanner} onClick={() => navigate("/highscores")}>
          <span style={{ animation: "arcadeFlash 0.8s step-start infinite", marginRight: 8 }}>🏆</span>
          <span style={styles.highScoreBannerText}>HIGH SCORES</span>
          <span style={{ animation: "arcadeFlash 0.8s step-start infinite", animationDelay: "0.4s", marginLeft: 8 }}>🏆</span>
        </div>

        {!player && (
          <div style={styles.arcadeBanner}>
            <span style={{ animation: "arcadeFlash 1s step-start infinite" }}>
              — LOG IN TO PLAY —
            </span>
          </div>
        )}

        <footer style={styles.footer}>
          {!player ? (
            <>
              <button style={styles.primaryBtn} onClick={() => navigate("/login")}>
                Create Account
              </button>
              <button style={styles.secondaryBtn} onClick={() => navigate("/login")}>
                Log In
              </button>
              <button style={styles.guestBtn} onClick={handleGuestLogin}>
                Play as Guest
              </button>
            </>
          ) : isGuest ? (
            <>
              <button style={styles.primaryBtn} onClick={() => navigate("/login")}>
                Create Account
              </button>
              <button style={styles.secondaryBtn} onClick={() => navigate("/login")}>
                Log In
              </button>
            </>
          ) : (
            <>
              {[
                { bundle: 1,  label: "1 game",   price: "$0.50" },
                { bundle: 5,  label: "5 games",  price: "$2.00" },
                { bundle: 10, label: "10 games", price: "$5.00" },
              ].map(({ bundle, label, price }) => (
                <button
                  key={bundle}
                  style={{ ...styles.buyBtn, opacity: buying === bundle ? 0.6 : 1 }}
                  onClick={() => handleBuyGame(bundle)}
                  disabled={!!buying}
                >
                  {buying === bundle ? "Redirecting…" : <>{label}<br /><span style={styles.buyPrice}>{price}</span></>}
                </button>
              ))}
              <button style={styles.secondaryBtn} onClick={() => navigate("/login")}>
                Switch Account
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "url(/arcade_room_realistic.jpg) center / cover no-repeat",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 12
  },
  card: {
    width: "100%",
    maxWidth: 420,
    background: "#ffffffee",
    borderRadius: 20,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)"
  },
  header: {
    padding: "24px 20px 16px",
    borderBottom: "1px solid #eee",
    textAlign: "center"
  },
  title: {
    margin: "0 0 8px",
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: "-0.5px"
  },
  subtitle: {
    margin: 0,
    fontSize: 14,
    color: "#666"
  },
  userBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontSize: 14,
    color: "#333"
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#007bff",
    cursor: "pointer",
    fontSize: 13,
    padding: 0,
    textDecoration: "underline"
  },
  main: {
    padding: "20px 16px 8px"
  },
  gameCard: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    padding: "18px 16px",
    borderRadius: 10,
    border: "3px solid #00d4ff",
    background: "repeating-linear-gradient(0deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 3px, rgba(0,212,255,0.04) 3px, rgba(0,212,255,0.04) 4px), linear-gradient(160deg, #06091a 0%, #0b1535 100%)",
    cursor: "pointer",
    textAlign: "left",
    position: "relative",
    transition: "box-shadow 0.2s, transform 0.1s",
    boxShadow: "0 0 10px rgba(0,212,255,0.5), 0 0 30px rgba(0,212,255,0.15), inset 0 0 20px rgba(0,212,255,0.05)"
  },
  gameCardState: {
    border: "3px solid #39ff14",
    background: "repeating-linear-gradient(0deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 3px, rgba(57,255,20,0.04) 3px, rgba(57,255,20,0.04) 4px), linear-gradient(160deg, #061206 0%, #0b2010 100%)",
    cursor: "pointer",
    boxShadow: "0 0 10px rgba(57,255,20,0.5), 0 0 30px rgba(57,255,20,0.15), inset 0 0 20px rgba(57,255,20,0.05)"
  },
  gameCardLeaders: {
    border: "3px solid #bf00ff",
    background: "repeating-linear-gradient(0deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 3px, rgba(191,0,255,0.04) 3px, rgba(191,0,255,0.04) 4px), linear-gradient(160deg, #100618 0%, #1a0b2e 100%)",
    cursor: "pointer",
    boxShadow: "0 0 10px rgba(191,0,255,0.5), 0 0 30px rgba(191,0,255,0.15), inset 0 0 20px rgba(191,0,255,0.05)"
  },
  gameCardDisabled: {
    border: "3px solid #444",
    background: "linear-gradient(160deg, #0e0e0e 0%, #1a1a1a 100%)",
    cursor: "default",
    opacity: 0.55,
    boxShadow: "none"
  },
  gameIcon: {
    fontSize: 36,
    lineHeight: 1,
    flexShrink: 0,
    filter: "drop-shadow(0 0 6px currentColor)"
  },
  gameName: {
    fontWeight: 700,
    fontSize: 15,
    marginBottom: 4,
    color: "#00d4ff",
    textShadow: "0 0 8px #00d4ff, 0 0 20px #00d4ff",
    fontFamily: "'Courier New', Courier, monospace",
    textTransform: "uppercase",
    letterSpacing: "1.5px"
  },
  gameDesc: {
    fontSize: 12,
    color: "#8ab8c8",
    lineHeight: 1.45,
    fontFamily: "'Courier New', Courier, monospace"
  },
  lockBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    fontSize: 11,
    color: "#888",
    background: "#eee",
    borderRadius: 8,
    padding: "2px 7px"
  },
  guestBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    fontSize: 11,
    color: "#856404",
    background: "#fff3cd",
    borderRadius: 8,
    padding: "2px 7px",
    fontWeight: 600
  },
  guestBtn: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 20,
    border: "1px solid #6c757d",
    background: "#fff",
    color: "#6c757d",
    fontSize: 15,
    cursor: "pointer"
  },
  footer: {
    padding: "16px",
    borderTop: "1px solid #eee",
    display: "flex",
    gap: 8,
    flexWrap: "wrap"
  },
  primaryBtn: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 20,
    border: "none",
    background: "#007bff",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer"
  },
  secondaryBtn: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 20,
    border: "1px solid #007bff",
    background: "#fff",
    color: "#007bff",
    fontSize: 15,
    cursor: "pointer"
  },
  highScoreBanner: {
    background: "#06091a",
    borderTop: "2px solid #ff0",
    borderBottom: "2px solid #ff0",
    padding: "11px 0",
    textAlign: "center",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    transition: "background 0.15s",
  },
  highScoreBannerText: {
    fontFamily: "'Courier New', Courier, monospace",
    fontWeight: 900,
    fontSize: 15,
    letterSpacing: 4,
    color: "#ff0",
    textShadow: "0 0 8px #ff0, 0 0 20px #ff0",
    textTransform: "uppercase",
  },
  arcadeBanner: {
    background: "#06091a",
    borderTop: "2px solid #ff0",
    borderBottom: "2px solid #ff0",
    padding: "10px 0",
    textAlign: "center",
    fontFamily: "'Courier New', Courier, monospace",
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: "2px",
    color: "#ff0",
    textShadow: "0 0 8px #ff0, 0 0 20px #ff0",
    boxShadow: "0 0 12px rgba(255,255,0,0.2)"
  },
  creditBadgeFunded: {
    fontSize: 11,
    fontWeight: 700,
    background: "#dcfce7",
    color: "#16a34a",
    borderRadius: 20,
    padding: "2px 8px",
  },
  creditBadgeEmpty: {
    fontSize: 11,
    fontWeight: 700,
    background: "#fee2e2",
    color: "#dc2626",
    borderRadius: 20,
    padding: "2px 8px",
  },
  buyBtn: {
    flex: 1,
    padding: "8px 6px",
    borderRadius: 14,
    border: "none",
    background: "#16a34a",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    lineHeight: 1.3,
  },
  buyPrice: {
    fontSize: 11,
    fontWeight: 400,
    opacity: 0.85,
  },
  paymentSuccess: {
    margin: "6px 0 0",
    fontSize: 13,
    color: "#16a34a",
    fontWeight: 600,
    textAlign: "center",
  },
};
